import React, {useState, useEffect} from 'react';
import {useAuth0} from '@auth0/auth0-react';
import {useNavigate} from 'react-router-dom';
import CreationFlowBar from 'src/components/CreationFlowBar';
import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import {Error as ErrorComponent} from 'src/components/Error';
import {createAnalysis, startAnalysis} from 'src/utils/bff-communication';
import {AssignmentOutlined, CheckCircleOutlineRounded, IntegrationInstructionsOutlined, SettingsOutlined} from '@mui/icons-material';
import ExpandableTableChip from '../ExpandableTableChip';
import FEReview from './FunctionalEnrichmentReview';
import {FunctionalEnrichmentParameters} from './FunctionalEnrichmentParameters';
import {getAppConfig} from 'src/utils/config';
import ChIPAnalysisDownStreamStep0 from '../ChIPAnalysisDownStreamStep0';
import {defaultChIPAnalysisbyExperiment, defaultChIPSampleGroups, DefaultChIPAnalyses} from 'src/utils/bff-communication';
import {DefaultChIPSampleGroup, resolveFunctionalEnrichmentGroupSelection} from 'src/utils/chip';

interface Step {
    name: string;
    caption: string;
    description: string;
    component: React.ReactNode;
    icon: React.ReactNode;
}

const FormContainer = styled(Box)({
    flex: 5,
    display: 'flex',
    flexDirection: 'column',
    width: '990px',
    minWidth: '600px',
    padding: '1.5% 2%',
    gap: 10
});

interface CreateFunctionalAnalysisProps {
    experiment: any;
    samples: any[];
}

export function CreateFunctionalAnalysis({experiment, samples}: CreateFunctionalAnalysisProps) {
    const {apiHost} = getAppConfig();
    const [ableToSave, setAbleToSave] = useState(false);
    const {getAccessTokenSilently} = useAuth0();
    const [error, setError] = useState<string>('');
    const [step, setStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const samplesD = samples;
    const [defaultChIPOptions, setDefaultChIPOptions] = useState<DefaultChIPAnalyses[]>([]);
    const [originalSampleGroups, setOriginalSampleGroups] = useState<DefaultChIPSampleGroup[]>([]);
    const [loadingSampleGroups, setLoadingSampleGroups] = useState(false);

    const [formData, setFormData] = useState<FEFormData>({
        type: 'Functional Enrichment',
        name: '',
        description: '',
        default_ChIP_analysis: '',
        sample_ids: [],
        functional_enrichment_analysis: {
            ontology: 'ALL',
            p_adjust_method: 'BH',
            q_value_cutoff: 0.05
        }
    });

    /**
     * Gets list of the default ChIP analyses for the current experiment.
     * Updates state.
     */
    const fetchDefaults = async (experiment_id: string, apiHost: string) => {
        try {
            const accessToken = await getAccessTokenSilently();
            const data = await defaultChIPAnalysisbyExperiment(experiment_id, apiHost, accessToken);

            setDefaultChIPOptions(data);
            setError('');
        } catch (err) {
            console.error('Error fetching default ChIP analyses:', err);
            setError(`${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    };

    useEffect(() => {
        fetchDefaults(experiment.id, apiHost);
    }, []);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            sample_ids: [],
            functional_enrichment_analysis: {
                ontology: 'ALL',
                p_adjust_method: 'BH',
                q_value_cutoff: 0.05
            }
        }));
        setOriginalSampleGroups([]);
        if (!formData.default_ChIP_analysis) return;

        let cancelled = false;
        setError('');
        setLoadingSampleGroups(true);
        const fetchSampleGroups = async () => {
            try {
                const accessToken = await getAccessTokenSilently();
                const groups = await defaultChIPSampleGroups(experiment.id, formData.default_ChIP_analysis, apiHost, accessToken);
                if (!cancelled) setOriginalSampleGroups(groups);
            } catch (err) {
                if (!cancelled) {
                    console.error('Error fetching default ChIP sample groups:', err);
                    setError(`${err instanceof Error ? err.message : 'Unknown error'}`);
                }
            } finally {
                if (!cancelled) setLoadingSampleGroups(false);
            }
        };
        fetchSampleGroups();

        return () => {
            cancelled = true;
        };
    }, [apiHost, experiment.id, formData.default_ChIP_analysis, getAccessTokenSilently]);

    const handleContinueClick = async (stepIndex: number) => {
        if (stepIndex < steps.length - 1) {
            setStep(stepIndex + 1);
        } else {
            console.log('Final step reached, ready to save analysis:', formData);
            setIsSubmitting(true);
            try {
                const accessToken = await getAccessTokenSilently();
                const expandedSampleIds = resolveFunctionalEnrichmentGroupSelection(formData.sample_ids, samplesD, originalSampleGroups);
                const updatedFormData = {
                    ...formData,
                    sample_ids: expandedSampleIds
                };
                const analysis = await createAnalysis(experiment, updatedFormData, apiHost, accessToken);
                const _startresponse = await startAnalysis(experiment.id, analysis.id, updatedFormData, apiHost, accessToken);
                navigate(`/experiment/${experiment.id}`);
            } catch (err) {
                console.error('Error creating analysis:', err);
                setError(`${err instanceof Error ? err.message : 'Unknown error'}`);
                setIsSubmitting(false);
            }
        }
    };

    const steps: Step[] = [
        {
            name: 'Analysis details',
            caption: 'Name your analysis, choose default ChIP analysis, and provide a description',
            description: 'Name your analysis, choose default ChIP analysis, and optionally provide a description.',
            component: (
                <ChIPAnalysisDownStreamStep0
                    formData={formData}
                    setFormData={setFormData}
                    handleNext={setAbleToSave}
                    error={error}
                    ChIPDefaultAnalysisList={defaultChIPOptions}
                />
            ),
            icon: <AssignmentOutlined sx={{fill: step === 0 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Select samples',
            caption: 'Indicate which samples you want to include in your analysis.',
            description: 'Indicate which samples should be included for analysis. You can edit samples in Manage Samples.',
            component: (
                <ExpandableTableChip
                    formData={formData}
                    setFormData={setFormData}
                    handleNext={setAbleToSave}
                    data={samplesD}
                    error={error}
                    setError={setError}
                    defaultChIPSampleGroups={originalSampleGroups}
                    loadingDefaultChIPSampleGroups={loadingSampleGroups}
                />
            ),
            icon: <IntegrationInstructionsOutlined sx={{fill: step === 1 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Edit analysis parameter settings',
            caption: 'Advanced configuration parameters for analysis.',
            description: 'Customize options for data analysis or use our default configuration.',
            component: <FunctionalEnrichmentParameters formData={formData} setFormData={setFormData} setValidForm={setAbleToSave} />,
            icon: <SettingsOutlined sx={{fill: step === 2 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Review analysis',
            caption: 'Check your setup before submitting for analysis.',
            description:
                'Confirm your analysis setup. Once an analysis has been started, its settings cannot be changed. If you want to run variations of this analysis, you will need to go through the setup process again.',
            component: <FEReview formData={formData} setFormData={setFormData} handleNext={setStep} samples={samplesD} />,
            icon: <CheckCircleOutlineRounded sx={{fill: step === 3 ? 'url(#linearColors)' : ''}} />
        }
    ];

    return (
        <>
            <CreationFlowBar title="SETUP NEW ANALYSIS" steps={steps} step={step} setStep={setStep} />
            <FormContainer>
                <Typography variant="headline" size="large">
                    {steps[step].name}
                </Typography>
                <Typography variant="body" size="medium" mb={2}>
                    {steps[step].description}
                </Typography>
                {steps[step].component}
                {error && <ErrorComponent message={error} />}
                <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={() => handleContinueClick(step)}
                    disabled={!ableToSave}
                    loading={isSubmitting}>
                    {step !== 3 ? 'Continue' : 'Run analysis'}
                </LoadingButton>
            </FormContainer>
        </>
    );
}
